import { action, computed, makeAutoObservable } from "mobx";
import Cookies from "js-cookie";
import jwtDecode from "jwt-decode";
import config from "../config";
import { SalesContext } from "../models/SalesContext";

const COOKIE_PREFIX = config.COOKIE_PREFIX || "";
const COOKIE_SUFFIX = "__authentigate_token_sanitized";

const SYSTEM_NAME = "t1sales";
const PORTAL_SYSTEM_NAME = "portal";
const ALL_SYSTEM_NAME = "ALL";
const BOXOFFICE_ADD_TO_CART = "BOXOFFICE_ADD_TO_CART";
const BOXOFFICE_ADD_TO_CART_AS_COMP = "BOXOFFICE_ADD_TO_CART_AS_COMP";
const ALL_OPERATION_NAME = "ALL";

interface Session {
    id: number;
    firstName: string;
    lastName: string;
    username: string;
    admin: boolean;
    kioskAccess: boolean;
    externalAccess: Array<{
        targetSystem: string;
        clientId: number;
        operation: string;
        parameters: { [key: string]: string | null };
    }>;
}

interface SessionState {
    loggedIn: boolean;
    latestToken: string;
    flashMessage: string;
    session: Session;
    loading: boolean;
    boxoffice: boolean;
}

const DefaultState = (): SessionState => {
    return {
        loggedIn: false,
        latestToken: "",
        flashMessage: "",
        session: {
            id: 0,
            firstName: "",
            lastName: "",
            username: "",
            admin: false,
            kioskAccess: false,
            externalAccess: [],
        },
        loading: false,
        boxoffice: true,
    };
};

const Now = () => new Date().getTime() / 1000; //Time in seconds

const LOCAL_STORAGE_BOXOFFICE_KEY =
    "authentigate.t1.boxoffice-" +
    (process.env.REACT_APP_DEPLOYMENT_NAME || "default");

export const COOKIE_BOXOFFICE_KEY = "__authentigate_t1_boxoffice";

class AuthStore {
    private _state: SessionState = DefaultState();
    private _onBoxOfficeChanged: (() => Promise<void>) | null = null;

    constructor() {
        makeAutoObservable(this);

        if (typeof window !== "undefined") {
            this.onLoad();
        }
    }

    @action onLoad() {
        this.readTokenFromLocalStorage();
        this.readBoxofficeFromLocalStorage();
        if (COOKIE_PREFIX === "dev") {
            const token = config.SET_DEV_TOKEN;
            if (token) {
                Cookies.set("dev__authentigate_token", token);
                Cookies.set("dev__authentigate_token_sanitized", token);
                try {
                    this.parseJwtFromCookie(token);
                    if (this._state.loggedIn) {
                        console.log("Using dev token from NEXT_PUBLIC_SET_DEV_TOKEN.");
                        return;
                    }
                    console.log("Dev token expired, falling back to fake session.");
                } catch (e) {
                    console.log("Invalid dev token, falling back to fake session.", e);
                }
            }
            this.fakeIt();
        }
    }

    @action toggleBoxOffice = async () => {
        this._state.boxoffice = !this._state.boxoffice;
        Cookies.set(
            COOKIE_BOXOFFICE_KEY,
            this._state.boxoffice ? "true" : "false",
            {
                secure: true,
            },
        );
        await this._onBoxOfficeChanged?.();
        location.reload();
    };

    @action
    setOnBoxOfficeChanged = (onBoxOfficeChanged: () => Promise<void>) => {
        this._onBoxOfficeChanged = onBoxOfficeChanged;
    };

    hasBoxofficeAccessForClient(clientIds: number[]): boolean {
        return (
            this.loggedIn &&
            (this.hasAdmin ||
                this._state.session.externalAccess.some(
                    (ea) =>
                        clientIds.indexOf(ea.clientId) >= 0 &&
                        (ea.targetSystem === SYSTEM_NAME ||
                            ea.targetSystem === ALL_SYSTEM_NAME) &&
                        (ea.operation === BOXOFFICE_ADD_TO_CART ||
                            ea.operation === BOXOFFICE_ADD_TO_CART_AS_COMP ||
                            ea.operation === ALL_OPERATION_NAME),
                ))
        );
    }

    hasBoxofficeAddToCartForDepartment(
        clientIds: Array<number>,
        departmentId: number,
    ): boolean {
        console.log(this.session.externalAccess);
        if (this.hasAdmin) return true;
        const departmentIdParameterizedAccess =
            this.session.externalAccess.filter(
                (provisioning) =>
                    clientIds.indexOf(provisioning.clientId) >= 0 &&
                    provisioning.targetSystem === SYSTEM_NAME &&
                    (provisioning.operation === BOXOFFICE_ADD_TO_CART ||
                        provisioning.operation ===
                            BOXOFFICE_ADD_TO_CART_AS_COMP) &&
                    !!provisioning.parameters.forDepartmentIds,
            );
        const departmentIdGeneralAccess = this.session.externalAccess.filter(
            (provisioning) =>
                clientIds.indexOf(provisioning.clientId) >= 0 &&
                (provisioning.targetSystem === SYSTEM_NAME ||
                    provisioning.targetSystem === ALL_SYSTEM_NAME) &&
                (provisioning.operation === BOXOFFICE_ADD_TO_CART ||
                    provisioning.operation === BOXOFFICE_ADD_TO_CART_AS_COMP ||
                    provisioning.operation === ALL_OPERATION_NAME) &&
                !provisioning.parameters.forDepartmentIds,
        );
        if (
            departmentIdParameterizedAccess.length > 0 &&
            (departmentIdParameterizedAccess.find(
                (provisioning) =>
                    provisioning.operation === BOXOFFICE_ADD_TO_CART,
            ) !== undefined ||
                departmentIdGeneralAccess.find(
                    (provisioning) =>
                        provisioning.operation === BOXOFFICE_ADD_TO_CART ||
                        provisioning.operation === ALL_OPERATION_NAME,
                ) === undefined)
        ) {
            return (
                departmentIdParameterizedAccess.find(
                    (provisioning) =>
                        provisioning.parameters.forDepartmentIds !== null &&
                        provisioning.parameters.forDepartmentIds
                            .split(",")
                            .indexOf("" + departmentId) >= 0,
                ) !== undefined
            );
        } else {
            return departmentIdGeneralAccess.length > 0;
        }
    }

    hasBoxofficeAddToCartAsCOMPForDepartment(
        clientIds: Array<number>,
        departmentId: number,
    ): boolean {
        if (this.hasAdmin) return true;
        const departmentIdParameterizedAccess =
            this.session.externalAccess.filter(
                (provisioning) =>
                    clientIds.indexOf(provisioning.clientId) >= 0 &&
                    provisioning.targetSystem === SYSTEM_NAME &&
                    provisioning.operation === BOXOFFICE_ADD_TO_CART_AS_COMP &&
                    !!provisioning.parameters.forDepartmentIds,
            );
        if (departmentIdParameterizedAccess.length > 0) {
            return (
                departmentIdParameterizedAccess.find(
                    (provisioning) =>
                        provisioning.parameters.forDepartmentIds !== null &&
                        provisioning.parameters.forDepartmentIds
                            .split(",")
                            .indexOf("" + departmentId) >= 0,
                ) !== undefined
            );
        } else {
            return (
                this.session.externalAccess.find(
                    (provisioning) =>
                        clientIds.indexOf(provisioning.clientId) >= 0 &&
                        (provisioning.targetSystem === SYSTEM_NAME ||
                            provisioning.targetSystem === ALL_SYSTEM_NAME) &&
                        (provisioning.operation ===
                            BOXOFFICE_ADD_TO_CART_AS_COMP ||
                            provisioning.operation === ALL_OPERATION_NAME) &&
                        !provisioning.parameters.forDepartmentIds,
                ) !== undefined
            );
        }
    }

    hasSuiteAccess(clientId: number): boolean {
        return true;
    }

    @computed get session(): Session {
        return this._state.session;
    }

    @computed get username(): string {
        if (this.loggedIn) {
            return this.session.username || "";
        } else {
            return "";
        }
    }

    @computed get fullname(): string {
        if (this.loggedIn) {
            return this.session.firstName + " " + this.session.lastName;
        } else {
            return "";
        }
    }

    @computed get firstName(): string {
        if (this.loggedIn) {
            return this.session.firstName;
        } else {
            return "";
        }
    }

    @computed get lastName(): string {
        if (this.loggedIn) {
            return this.session.lastName;
        } else {
            return "";
        }
    }

    @computed get loggedIn(): boolean {
        return this._state.loggedIn;
    }

    @computed get tokenString(): string {
        return this._state.latestToken;
    }

    @computed get hasAdmin(): boolean {
        return this._state.loggedIn && this._state.session.admin;
    }

    @computed get hasKioskAccess(): boolean {
        return this._state.loggedIn && this._state.session.kioskAccess;
    }

    @computed get flashMessage(): string {
        return this._state.flashMessage;
    }

    @computed get loading(): boolean {
        return this._state.loading;
    }

    @computed get boxoffice(): boolean {
        return this.loggedIn && this._state.boxoffice;
    }

    @computed get salesContext(): SalesContext {
        return this.boxoffice ? SalesContext.BOXOFFICE : SalesContext.ECOMMERCE;
    }

    @computed get developer(): boolean {
        return this._state.session.id === 5;
    }

    private parseJwtFromCookie(tokenString: string) {
        this._state.latestToken = tokenString;
        const token = jwtDecode<any>(tokenString);
        console.log("Found cookie");
        if (token && token.exp - Now() > 0) {
            console.log("Session valid.");
            this._state.loggedIn = true;
            this._state.session = {
                id: token.id,
                firstName: token.firstName,
                lastName: token.lastName,
                username: token.username,
                admin: token.admin,
                kioskAccess: token.kioskAccess,
                externalAccess: token.externalAccess,
            };
        } else {
            console.log("Session expired.");
            this._state = DefaultState();
        }
    }

    private updateLocalStorage(key: string, value: string) {
        localStorage.setItem(key, value);
    }

    private clearLocalStorage(key: string) {
        localStorage.removeItem(key);
    }

    private readTokenFromLocalStorage() {
        const cookieToken = Cookies.get(`${COOKIE_PREFIX}${COOKIE_SUFFIX}`);
        if (cookieToken) {
            this.parseJwtFromCookie(cookieToken);
            return;
        }
    }

    private readBoxofficeFromLocalStorage() {
        const boxoffice = Cookies.get(COOKIE_BOXOFFICE_KEY);
        this._state.boxoffice = boxoffice === "true" ? true : false;
    }

    private fakeIt() {
        console.log(
            "Auth disabled, using dummy credentials. Server must have auth disabled for this to work too.",
        );
        this._state.loggedIn = true;
        this._state.latestToken = "abc";
        this._state.session = {
            id: 1,
            firstName: "Jordan",
            lastName: "Farnell TEST",
            username: "testtest",
            admin: true,
            kioskAccess: true,
            externalAccess: [],
        };
    }
}

export default new AuthStore();
